import {AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {UserProfileDto} from '../../../models/generated-models';
import {normalizeUserRoles} from '../../app.constants';
import {UserAddressOptionDto} from '../../../models/generated-models';

/** Field-level validators shared by the profile edit page and onboarding wizard. */
export class ProfileValidators {
  static thaiPattern(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }
    const thaiRegex = /^[\u0e00-\u0e7f\s\-']+$/;
    return thaiRegex.test(value) ? null : {'thaiPattern': true};
  }

  static englishPattern(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }
    const englishRegex = /^[A-Za-z\s\-']+$/;
    return englishRegex.test(value) ? null : {'englishPattern': true};
  }

  static phonePattern(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }
    const phoneRegex = /^0[0-9]{8,9}$/;
    return phoneRegex.test(value.replace(/[-\s]/g, '')) ? null : {'phonePattern': true};
  }

  static postalCodePattern(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) {
      return null;
    }
    const postalRegex = /^[0-9]{5}$/;
    return postalRegex.test(value) ? null : {'postalCodePattern': true};
  }
}

/** Build the canonical profile FormGroup used by both the edit page and the wizard. */
export function buildProfileForm(fb: FormBuilder): FormGroup {
  return fb.group({
    status: [true],
    userRoles: fb.array([], Validators.required),
    personalInfo: fb.group({
      cardType: ['ID_CARD', Validators.required],
      cardNo: ['', [Validators.required, Validators.pattern(/^[A-Za-z0-9]{1,13}$/)]],
      birthdate: ['', Validators.required],
      prefixName: ['', Validators.required],
      firstName: ['', [Validators.required, ProfileValidators.thaiPattern]],
      middleName: [''],
      lastName: ['', [Validators.required, ProfileValidators.thaiPattern]],
      prefixNameEn: [''],
      firstNameEn: ['', [ProfileValidators.englishPattern]],
      middleNameEn: [''],
      lastNameEn: ['', [ProfileValidators.englishPattern]],
      mobile: ['', [Validators.required, ProfileValidators.phonePattern]],
      email: ['', [Validators.required, Validators.email]],
      phoneNo: ['', ProfileValidators.phonePattern],
      faxNo: ['']
    }),
    address: fb.group({
      addressNo: ['', Validators.required],
      moo: [''],
      soi: [''],
      road: [''],
      location: [''],
      subDistrict: [null, Validators.required],
      subDistrictId: [null],
      subdistrictName: [''],
      districtId: [null],
      districtName: [''],
      provinceId: [null],
      provinceName: [''],
      postCode: ['', ProfileValidators.postalCodePattern]
    })
  });
}

/** Populate the form from a loaded profile; returns the derived summary + completion flag. */
export function applyProfileToForm(fb: FormBuilder, form: FormGroup, profile: UserProfileDto): {summary: UserProfileDto; completed: boolean} {
  const normalizedUserRoles = normalizeUserRoles(profile.userRoles);
  const primaryUserRole = normalizedUserRoles[0] ?? null;
  const completed = profile.profileCompleted ?? true;
  const summary: UserProfileDto = {
    id: profile.id ?? null,
    username: profile.username || '-',
    email: profile.email || '-',
    cardNo: profile.cardNo || '-',
    fullName: profile.fullName || '-'
  };

  const userRoles = form.get('userRoles') as FormArray;
  userRoles.clear({emitEvent: false});
  if (primaryUserRole) {
    userRoles.push(fb.control(primaryUserRole), {emitEvent: false});
  }

  form.patchValue({
    status: profile.status ?? true,
    personalInfo: {
      cardType: profile.cardType || (profile.hasIdCard ? 'ID_CARD' : 'PASSPORT'),
      cardNo: profile.cardNo ?? '',
      birthdate: profile.birthdate ?? '',
      prefixName: profile.prefixName ?? '',
      firstName: profile.firstName ?? '',
      middleName: profile.middleName ?? '',
      lastName: profile.lastName ?? '',
      prefixNameEn: profile.prefixNameEn ?? '',
      firstNameEn: profile.firstNameEn ?? '',
      middleNameEn: profile.middleNameEn ?? '',
      lastNameEn: profile.lastNameEn ?? '',
      mobile: profile.mobile ?? '',
      email: profile.email ?? '',
      phoneNo: profile.phoneNo ?? '',
      faxNo: profile.faxNo ?? ''
    },
    address: {
      addressNo: profile.addressNo ?? '',
      moo: profile.moo ?? '',
      soi: profile.soi ?? '',
      road: profile.road ?? '',
      location: profile.location ?? '',
      subDistrict: profile.subDistrictId ? {
        subdistrictId: profile.subDistrictId,
        text: profile.location ?? profile.subDistrictName ?? '',
        subdistrictTitle: profile.subDistrictName ?? '',
        districtTitle: profile.districtName ?? '',
        provinceTitle: profile.provinceName ?? '',
        districtId: profile.districtId ?? null,
        provinceId: profile.provinceId ?? null,
        zipcode: profile.postCode ?? ''
      } : null,
      subDistrictId: profile.subDistrictId ?? null,
      subdistrictName: profile.subDistrictName ?? '',
      districtId: profile.districtId ?? null,
      districtName: profile.districtName ?? '',
      provinceId: profile.provinceId ?? null,
      provinceName: profile.provinceName ?? '',
      postCode: profile.postCode ?? ''
    }
  }, {emitEvent: false});

  return {summary, completed};
}

/** Build the save payload from the current form value. */
export function buildSavePayload(form: FormGroup, summary: UserProfileDto, profileCompleted: boolean): UserProfileDto {
  const value = form.getRawValue();
  const selectedUserRoles = normalizeUserRoles((value.userRoles as unknown[]) ?? []).slice(0, 1);
  return {
    id: summary.id,
    username: summary.username,
    email: value.personalInfo.email || '',
    cardNo: value.personalInfo.cardNo || '',
    fullName: [value.personalInfo.prefixName, value.personalInfo.firstName, value.personalInfo.lastName]
      .filter((item: string) => !!item).join(' ').trim(),
    status: Boolean(value.status),
    userRoles: selectedUserRoles,
    hasIdCard: value.personalInfo.cardType === 'ID_CARD',
    cardType: value.personalInfo.cardType || '',
    birthdate: value.personalInfo.birthdate || '',
    prefixName: value.personalInfo.prefixName || '',
    firstName: value.personalInfo.firstName || '',
    middleName: value.personalInfo.middleName || '',
    lastName: value.personalInfo.lastName || '',
    prefixNameEn: value.personalInfo.prefixNameEn || '',
    firstNameEn: value.personalInfo.firstNameEn || '',
    middleNameEn: value.personalInfo.middleNameEn || '',
    lastNameEn: value.personalInfo.lastNameEn || '',
    mobile: value.personalInfo.mobile || '',
    phoneNo: value.personalInfo.phoneNo || '',
    faxNo: value.personalInfo.faxNo || '',
    addressNo: value.address.addressNo || '',
    moo: value.address.moo || '',
    soi: value.address.soi || '',
    road: value.address.road || '',
    location: value.address.location || '',
    subDistrictId: value.address.subDistrictId ?? null,
    subDistrictName: value.address.subdistrictName || '',
    districtId: value.address.districtId ?? null,
    districtName: value.address.districtName || '',
    provinceId: value.address.provinceId ?? null,
    provinceName: value.address.provinceName || '',
    postCode: value.address.postCode || '',
    profileCompleted: profileCompleted,
    profileImageUrl: ''
  };
}

/** Fill the readonly อำเภอ / จังหวัด / รหัสไปรษณีย์ fields from a selected ตำบล option. */
export function applySubDistrictSelection(form: FormGroup, option: UserAddressOptionDto | null): void {
  if (!option) {
    form.get('address')?.patchValue({
      location: '', subDistrictId: null, subdistrictName: '',
      districtId: null, districtName: '', provinceId: null, provinceName: '', postCode: ''
    }, {emitEvent: false});
    return;
  }
  const location = [option.subdistrictTitle, option.districtTitle, option.provinceTitle].filter(Boolean).join(' >> ');
  form.get('address')?.patchValue({
    location,
    subDistrictId: option.subdistrictId ?? null,
    subdistrictName: option.subdistrictTitle ?? '',
    districtId: option.districtId ?? null,
    districtName: option.districtTitle ?? '',
    provinceId: option.provinceId ?? null,
    provinceName: option.provinceTitle ?? '',
    postCode: option.zipcode ?? ''
  }, {emitEvent: false});
}

export function maskPhone(raw: string): string {
  return raw.replace(/[^0-9]/g, '').slice(0, 10);
}

export function maskId(raw: string): string {
  return raw.replace(/[^A-Za-z0-9]/g, '').slice(0, 13);
}

export function maskPostal(raw: string): string {
  return raw.replace(/[^0-9]/g, '').slice(0, 5);
}
